import { useEffect, useRef, useState } from "react";

export const STAGE_W = 1280;
export const STAGE_H = 720;

/** Track an element's width and return the scale that fits a 1280px stage. */
export function useStageScale() {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.2);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth || 0;
      if (w > 0) setScale(w / STAGE_W);
    };
    update();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  return [ref, scale];
}

/**
 * Which slides an action applies to.
 * value: "current" | "all" | number[] (0-based slide indices)
 */
export function ScopePicker({ value, onChange, slideCount, currentIndex }) {
  const isList = Array.isArray(value);
  const chosen = isList ? value : [];
  return (
    <div>
      <div className="cine-chips" role="radiogroup" aria-label="Apply to">
        <button
          type="button"
          role="radio"
          aria-checked={value === "current"}
          className={`cine-chip${value === "current" ? " is-active" : ""}`}
          onClick={() => onChange("current")}
        >
          This slide
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={value === "all"}
          className={`cine-chip${value === "all" ? " is-active" : ""}`}
          onClick={() => onChange("all")}
        >
          All slides
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={isList}
          className={`cine-chip${isList ? " is-active" : ""}`}
          onClick={() => onChange(isList ? value : [currentIndex])}
        >
          Choose slides…
        </button>
      </div>
      {isList ? (
        <div className="cine-chips" style={{ marginTop: 8 }} aria-label="Slides">
          {Array.from({ length: slideCount }, (_, i) => {
            const on = chosen.includes(i);
            return (
              <button
                key={i}
                type="button"
                aria-pressed={on}
                className={`cine-chip${on ? " is-active" : ""}`}
                style={{ minWidth: 34 }}
                onClick={() =>
                  onChange(on ? chosen.filter((n) => n !== i) : [...chosen, i].sort((a, b) => a - b))
                }
              >
                {i + 1}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export function scopeLabel(scope, slideCount) {
  if (scope === "current") return "this slide";
  if (scope === "all") return `all ${slideCount} slides`;
  if (Array.isArray(scope)) return `${scope.length} slide${scope.length === 1 ? "" : "s"}`;
  return "";
}

export function RangeField({ label, value, min, max, step = 1, unit = "", onChange, onCommit }) {
  return (
    <label className="cine-slider">
      <span>{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onMouseUp={onCommit}
        onTouchEnd={onCommit}
        onKeyUp={onCommit}
      />
      <output>
        {value}
        {unit}
      </output>
    </label>
  );
}
